import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Star } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Link } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

type AlumniProfileData = {
  id: string;
  name: string;
  avatar_url: string | null;
  school?: string;
  expertise?: string;
  services?: {
    id: string;
    title: string;
  }[];
  rating?: number;
};

const AlumniCard = ({ alumni }: { alumni: AlumniProfileData }) => {
  const expertiseTags = alumni.expertise ? alumni.expertise.split(',').map(tag => tag.trim()).filter(Boolean) : [];
  
  const initials = alumni.name
    .split(' ')
    .map(name => name[0])
    .join('')
    .toUpperCase();
  
  const displayTags = expertiseTags.length > 0 
    ? expertiseTags.slice(0, 3) 
    : alumni.services?.slice(0, 3).map(service => service.title) || [];
  
  return (
    <Link to={`/alumni/${alumni.id}`} state={{ alumniData: alumni }} className="min-w-[280px] max-w-xs flex-shrink-0 mx-4">
      <Card className="bg-white shadow-md hover:shadow-lg transition-shadow duration-300 hover:scale-[1.02]">
        <div className="p-5">
          <div className="flex items-center mb-4">
            <Avatar className="h-16 w-16 border-2 border-crimson">
              <AvatarImage src={alumni.avatar_url || ''} />
              <AvatarFallback className="bg-crimson text-white">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="ml-3">
              <h3 className="font-semibold text-gray-800">{alumni.name}</h3>
              <p className="text-sm text-gray-600">{alumni.school || "University"}</p>
            </div>
          </div>
          
          <div className="flex items-center mb-3">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={14}
                  className={i < Math.floor(alumni.rating || 4.5) ? "text-yellow-500 fill-yellow-500" : "text-gray-300"}
                />
              ))}
            </div>
            <span className="ml-1 text-sm font-medium text-gray-600">{alumni.rating || 4.5}</span>
          </div>
          
          <div className="mb-4">
            {displayTags.map((skill, idx) => (
              <Badge key={idx} variant="outline" className="mr-1 mb-1 bg-silver/10">
                {skill}
              </Badge>
            ))}
          </div>
        </div>
      </Card>
    </Link>
  );
};

const AlumniCarousel = () => {
  const [alumniProfiles, setAlumniProfiles] = useState<AlumniProfileData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfiles = async () => {
      try {
        const { data, error } = await supabase
          .from('alumni_profiles')
          .select(`
            id,
            profiles(first_name, last_name, avatar_url),
            school,
            expertise
          `)
          .limit(8);

        if (error) throw error;
        
        if (data && data.length) {
          const profilesWithServices = await Promise.all(data.map(async (profile: any) => {
            const { data: services, error: servicesError } = await supabase
              .from('alumni_services')
              .select('id, title')
              .eq('alumni_id', profile.id);
            
            if (servicesError) {
              console.error('Error fetching services:', servicesError);
              return { ...profile, services: [] };
            }
            
            const { data: reviews, error: reviewError } = await supabase
              .from('reviews')
              .select('rating')
              .eq('alumni_id', profile.id);
            
            if (reviewError) console.error('Error fetching reviews:', reviewError);
            
            let avgRating = 0;
            if (reviews && reviews.length) {
              avgRating = reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviews.length;
            } else {
              avgRating = 4.5 + Math.random() * 0.5;
            }
            
            const formattedAlumni: AlumniProfileData = {
              id: profile.id,
              name: `${profile.profiles?.first_name || ''} ${profile.profiles?.last_name || ''}`.trim() || "Alumni",
              avatar_url: profile.profiles?.avatar_url || null,
              school: profile.school || "",
              expertise: profile.expertise || "",
              services: services || [],
              rating: parseFloat(avgRating.toFixed(1))
            };
            
            return formattedAlumni;
          }));
          
          setAlumniProfiles(profilesWithServices);
        } else {
          setAlumniProfiles([]);
        }
      } catch (err: any) {
        console.error('Error fetching alumni profiles:', err);
        setError(err.message || 'Failed to fetch alumni profiles');
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfiles();
  }, []);

  return (
    <section className="py-16 bg-white">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">Featured Alumni</h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Connect with experienced alumni from top institutions who are ready to help you succeed
          </p>
        </div>
        
        {isLoading ? (
          <div className="flex justify-center">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="w-[280px] h-64 rounded-lg bg-gray-100 animate-pulse"></div>
              ))}
            </div>
          </div>
        ) : error ? (
          <div className="text-center text-red-500">
            <p>Error loading alumni profiles. Please try again later.</p>
          </div>
        ) : alumniProfiles.length > 0 ? (
          <div className="relative">
            <div className="overflow-hidden">
              <div className="flex py-4 space-x-4 animate-carousel">
                {alumniProfiles.map((alumni) => (
                  <AlumniCard key={alumni.id} alumni={alumni} />
                ))}
                {alumniProfiles.map((alumni) => (
                  <AlumniCard key={`dup-${alumni.id}`} alumni={alumni} />
                ))}
              </div>
            </div>
            
            <div className="absolute top-0 bottom-0 left-0 w-16 bg-gradient-to-r from-white to-transparent z-10"></div>
            <div className="absolute top-0 bottom-0 right-0 w-16 bg-gradient-to-l from-white to-transparent z-10"></div>
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500">No alumni profiles available yet. Check back soon!</p>
          </div>
        )}
        
        <div className="mt-8 text-center">
          <Button variant="outline" className="border-crimson text-crimson hover:bg-crimson/10" asChild>
            <Link to="/browse">Browse All Alumni</Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default AlumniCarousel;
